import React, { useMemo, useState } from 'react'
import {
  SwipeableDrawer,
  Typography,
  IconButton,
  Chip,
  Button,
} from '@material-ui/core'
import {
  Close as CloseIcon,
  NotificationsOutlined,
  AccessAlarm,
  OpenInNew,
} from '@material-ui/icons'
import { EmptySearchResultComponent } from '../../../components/empty-search-result-component/empty-search-result.component'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useBundleNotificationsDrawerStyles } from './bundle-notifications-drawer.styles'
import { setBundleDrawerOpen } from '../../../store/notifications/reducer'
import { selectIsBundleDrawerOpen } from '../../../store/notifications/selectors'
import { UserNotification } from '../../../store/notifications/types'
import { useBundleNotificationsDrawer } from './bundle-notifications-drawer.hook'
import { LabeledSelect } from '../../../shared/labeled-select'

/**
 * Converts ISO date string to American format (MM/DD/YYYY, HH:MM AM/PM)
 * @param isoDateString - ISO format date string (e.g., "2026-01-09T16:41:17.55")
 * @returns Formatted date string in American format
 */
const formatToAmericanDate = (isoDateString: string): string => {
  const date = new Date(isoDateString)
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const year = date.getFullYear()

  let hours = date.getHours()
  const minutes = date.getMinutes().toString().padStart(2, '0')
  const ampm = hours >= 12 ? 'PM' : 'AM'
  hours = hours % 12
  hours = hours ? hours : 12 // the hour '0' should be '12'
  const formattedHours = hours.toString().padStart(2, '0')

  return `${month}/${day}/${year}, ${formattedHours}:${minutes} ${ampm}`
}

export const BundleNotificationsDrawer: React.FC = () => {
  const classes = useBundleNotificationsDrawerStyles()
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const isOpened = useSelector(selectIsBundleDrawerOpen)
  const [selectedTab, setSelectedTab] = useState(0)

  const { notifications, loading, markAsReviewedHandler } =
    useBundleNotificationsDrawer()

  const readTypeOptions = useMemo(
    () => [
      { value: 0, label: t('notifications.all', 'All') },
      { value: 1, label: t('notifications.unread', 'Unread') },
      { value: 2, label: t('notifications.read', 'Read') },
    ],
    [t]
  )

  const handleClose = () => {
    dispatch(setBundleDrawerOpen(false))
  }

  const handleOpen = () => {
    // do nothing, handler is required by drawer
  }

  const handleSeeAll = () => {
    dispatch(setBundleDrawerOpen(false))
    navigate('/notifications')
  }

  const handleReadTypeChange = (newValue: number) => {
    setSelectedTab(newValue)
  }

  const filteredNotifications = (): UserNotification[] => {
    if (selectedTab === 0) {
      return notifications
    } else if (selectedTab === 1) {
      return notifications.filter((n: UserNotification) => !n.reviewed)
    } else {
      return notifications.filter((n: UserNotification) => n.reviewed)
    }
  }

  const displayNotifications = filteredNotifications()

  return (
    <SwipeableDrawer
      anchor="right"
      open={isOpened}
      onClose={handleClose}
      onOpen={handleOpen}
      ModalProps={{
        BackdropProps: {
          className: classes.backdropBlur,
        },
      }}
    >
      <div className={classes.drawerContent}>
        <div className={classes.header}>
          <Typography variant="h6" className={classes.title}>
            {t('notifications.title', 'Notifications')}
          </Typography>
          <IconButton
            className={classes.closeButton}
            onClick={handleClose}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </div>

        {/* Status filter */}
        {notifications.length > 0 && (
          <LabeledSelect
            label={t('notifications.statusFilter')}
            value={selectedTab}
            options={readTypeOptions}
            onChange={handleReadTypeChange}
            idSuffix="bundle-notifications-read-status"
            className={classes.readTypeSelect}
            fluid
          />
        )}

        <div className={classes.content}>
          {displayNotifications.length > 0 ? (
            <div className={classes.notificationsSection}>
              <div className={classes.notificationsList}>
                {displayNotifications.map((notification) => (
                  <div
                    key={notification.userNotificationId}
                    className={classes.notificationItem}
                  >
                    <div className={classes.notificationHeader}>
                      <NotificationsOutlined
                        className={classes.notificationIcon}
                      />
                      <Chip
                        label={
                          notification.reviewed
                            ? t('notifications.item.read')
                            : t('notifications.item.new')
                        }
                        color={notification.reviewed ? 'default' : 'secondary'}
                        size="small"
                        className={classes.notificationChip}
                      />
                    </div>
                    <Typography
                      variant="subtitle1"
                      className={classes.notificationSubject}
                    >
                      {notification.subject}
                    </Typography>
                    {notification.text && (
                      <Typography
                        variant="body2"
                        className={classes.notificationText}
                      >
                        {notification.text}
                      </Typography>
                    )}
                    {notification.orderLink && (
                      <Button
                        variant="outlined"
                        size="small"
                        href={notification.orderLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={classes.orderLinkButton}
                        startIcon={<OpenInNew />}
                      >
                        {t('notifications.item.viewOrder', 'View Order')}
                      </Button>
                    )}
                    <div className={classes.notificationDate}>
                      <AccessAlarm className={classes.dateIcon} />
                      <Typography
                        variant="caption"
                        className={classes.dateCaption}
                      >
                        {formatToAmericanDate(notification.createdUtc)}
                      </Typography>
                    </div>
                    {!notification.reviewed && (
                      <Button
                        size="small"
                        className={classes.markAsReadButton}
                        onClick={() =>
                          markAsReviewedHandler(notification.userNotificationId)
                        }
                        disabled={loading}
                      >
                        {t('notifications.item.markAsRead', 'Mark as read')}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className={classes.emptyStateWrap}>
              <EmptySearchResultComponent
                title={
                  selectedTab === 1
                    ? t('notifications.noUnread', 'No unread notifications')
                    : selectedTab === 2
                    ? t('notifications.noRead', 'No read notifications')
                    : t('notifications.noNotifications', 'No notifications')
                }
                icon={NotificationsOutlined}
              />
            </div>
          )}
        </div>

        {/* Fixed button at the bottom */}
        {notifications.length > 0 && (
          <div className={classes.footerButton}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSeeAll}
              className={classes.seeAllButton}
            >
              {t('notifications.seeAll', 'See all')}
            </Button>
          </div>
        )}
      </div>
    </SwipeableDrawer>
  )
}
